import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  LayoutDashboard,
  Car,
  Building2,
  Users,
  Zap,
  Droplets,
  Home,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  UserCog,
  FileText
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Layout = () => {
  const { user, token, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await axios.get(`${API}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadCount(response.data.count);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    if (token) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [token]);

  const navigation = [
    { name: 'لوحة التحكم', href: '/dashboard', icon: LayoutDashboard },
    { name: 'السيارات', href: '/cars', icon: Car },
    { name: 'الصكوك العقارية', href: '/real-estate', icon: Building2 },
    { name: 'العمالة المنزلية', href: '/workers', icon: Users },
    { name: 'الإيجارات', href: '/rentals', icon: Home },
    { name: 'عدادات الكهرباء', href: '/electricity', icon: Zap },
    { name: 'عدادات المياه', href: '/water', icon: Droplets },
    { name: 'الإشعارات', href: '/notifications', icon: Bell, badge: unreadCount },
    { name: 'التقارير', href: '/reports', icon: FileText },
    ...(isAdmin ? [{ name: 'إدارة المستخدمين', href: '/users', icon: UserCog }] : []),
  ];

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.href;
    return (
      <Link
        to={item.href}
        data-testid={`nav-${item.href.replace('/', '')}`}
        className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all rounded-lg ${
          isActive ? 'bg-white/15' : 'hover:bg-white/10'
        }`}
        style={{ direction: 'rtl' }}
        onClick={() => setMobileMenuOpen(false)}
      >
        <item.icon className="w-5 h-5 flex-shrink-0" />
        <span className={!sidebarOpen ? 'hidden' : 'flex-1'}>{item.name}</span>
        {item.badge > 0 && (
          <Badge variant="destructive" className="text-xs px-2 py-0">
            {item.badge}
          </Badge>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed right-0 top-0 h-screen text-white transition-all duration-300 z-40 ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
        style={{ background: 'linear-gradient(180deg, #1a365d 0%, #0f2744 100%)' }}
        data-testid="desktop-sidebar"
      >
        {/* Logo */}
        <div className="p-4 border-b border-white/10">
          <div className="flex flex-col items-center justify-center">
            <img 
              src="/assets/logo.png" 
              alt="الشعار" 
              className={`object-contain flex-shrink-0 ${sidebarOpen ? 'w-32 h-32' : 'w-14 h-14'}`}
            />
            {sidebarOpen && (
              <p className="text-xs text-amber-400 mt-2 text-center">نظام إدارة الشؤون الخاصة</p>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto" style={{ direction: 'rtl' }}>
          {navigation.map((item) => (
            <NavLink key={item.href} item={item} />
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-white/10">
          {sidebarOpen && (
            <div className="mb-3 px-3 text-right">
              <p className="font-medium text-sm">{user?.username}</p>
              <p className="text-xs text-amber-400/80">
                {isAdmin ? 'مدير النظام' : 'مشاهد'}
              </p>
            </div>
          )}
          <Button
            variant="ghost"
            className="w-full flex items-center gap-3 text-white/80 hover:text-white hover:bg-white/10"
            style={{ direction: 'rtl' }}
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>تسجيل الخروج</span>}
          </Button>
        </div>

        {/* Collapse Button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute -left-3 top-1/2 transform -translate-y-1/2 border border-white/20 rounded-full w-6 h-6 text-white"
          style={{ background: '#1a365d' }}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="collapse-sidebar"
        >
          <ChevronLeft className={`w-4 h-4 transition-transform ${!sidebarOpen ? 'rotate-180' : ''}`} />
        </Button>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 right-0 left-0 h-16 text-white z-50 flex items-center justify-between px-4" style={{ background: '#1a365d' }}>
        <div className="flex items-center gap-3">
          <img 
            src="/assets/logo.png" 
            alt="الشعار" 
            className="h-10 object-contain"
          />
          <span className="text-xs text-amber-400">نظام إدارة الشؤون الخاصة</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="text-white"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 text-white z-40 animate-fade-in" style={{ background: '#1a365d' }}>
          <ScrollArea className="h-full">
            <nav className="p-4 space-y-1">
              {navigation.map((item) => (
                <NavLink key={item.href} item={item} />
              ))}
              <div className="pt-4 border-t border-white/10 mt-4">
                <div className="px-4 py-2">
                  <p className="font-medium">{user?.username}</p>
                  <p className="text-xs text-amber-400">{isAdmin ? 'مدير النظام' : 'مشاهد'}</p>
                </div>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10"
                  onClick={logout}
                >
                  <LogOut className="w-5 h-5 ml-3" />
                  تسجيل الخروج
                </Button>
              </div>
            </nav>
          </ScrollArea>
        </div>
      )}

      {/* Main Content */}
      <main
        className={`flex-1 transition-all duration-300 ${
          sidebarOpen ? 'lg:mr-64' : 'lg:mr-20'
        } mt-16 lg:mt-0`}
      >
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
